import { linkOptions } from '@tanstack/react-router';

import { CustomerCompanyMessage } from '../../../../../types/CustomerCompanyMessage';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { formatDateTime } from '../../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { useRole } from '../../../../common/hooks/useRole';
import { IconCheck } from '../../../../common/icons/cdl/Check';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { TableEmptyState } from '../../../../common/TableEmptyState/TableEmptyState';
import { useCustomerList } from '../hooks/useCustomerList';

interface CustomersTableProps {
    groupId: string;
}

export const CustomersTable = (props: CustomersTableProps) => {
    const customersQuery = useCustomerList({ groupId: props.groupId });
    const role = useRole();

    if (customersQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (customersQuery.isError) {
        return <GenericError />;
    }

    if (!customersQuery.data.length) {
        return <TableEmptyState text={translate('customers.emptylist')} />;
    }

    const reversedCustomers = [...customersQuery.data].reverse();

    return (
        <TableBuilder
            data={reversedCustomers}
            rowLink={(row: CustomerCompanyMessage) => {
                return linkOptions({
                    to: '/customer-groups/$groupId/companies/$companyId',
                    params: { groupId: row.customerGroupId, companyId: row.id },
                });
            }}
            grow={false}
        >
            <TableBuilderColumn<CustomerCompanyMessage> header={translate('customer.name')} width="30%" truncate>
                {(row) => formatCompanyName({ company: row, forcePublicName: true })}
            </TableBuilderColumn>
            <TableBuilderColumn<CustomerCompanyMessage> header={translate('company.internalName')} width="20%" truncate>
                {(row) => row.internalName || null}
            </TableBuilderColumn>
            <TableBuilderColumn<CustomerCompanyMessage> header={translate('global.email')} width="25%" truncate>
                {(row) => row.email}
            </TableBuilderColumn>
            <TableBuilderColumn<CustomerCompanyMessage> header={translate('customer.datecreated')}>
                {(row) => formatDateTime({ date: row.dateCreated })}
            </TableBuilderColumn>
            {role.isSuperAdmin() ? (
                <TableBuilderColumn<CustomerCompanyMessage> header={translate('customer.testcompany')}>
                    {(row) => (row.testCompany ? <IconCheck width={16} height={16} /> : null)}
                </TableBuilderColumn>
            ) : null}
        </TableBuilder>
    );
};
